import {
  AlertCircle,
  Calendar,
  CheckCircle,
  Clock,
  IndianRupee,
  ShoppingCart,
  Star,
  ThumbsDown,
  ThumbsUp,
  User,
  Wrench,
  X,
  XCircle,
} from 'lucide-react';
import { useState } from 'react';
import { formatDateTime } from 'utils/dateFormatter';
import { useToggleServiceStatus } from '../hooks/useToggleServiceStatus';

export const ViewServiceModal = (props) => {
  const { isViewModalOpen, setIsViewModalOpen, service } = props;
  const { mutate: updateStatus, isPending } = useToggleServiceStatus();
  const [showRejectInput, setShowRejectInput] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');

  if (!isViewModalOpen || !service) return null;

  // Helper function to extract image/video URL from malformed API response
  const getMediaUrl = (mediaItem) => {
    if (!mediaItem) return null;

    // If it's a string with 'url' property, use that
    if (mediaItem?.url && typeof mediaItem.url === 'string') {
      return mediaItem.url;
    }

    // If it's already a plain string, use it
    if (typeof mediaItem === 'string') {
      return mediaItem;
    }

    // If it's an object with numeric keys (malformed), reconstruct the URL
    if (typeof mediaItem === 'object' && mediaItem !== null) {
      // Check if it has 'url' property that's an object with numeric keys
      if (mediaItem.url && typeof mediaItem.url === 'object') {
        const urlChars = Object.values(mediaItem.url);
        return urlChars.join('');
      }
      // Check if the media itself is an object with numeric keys
      const chars = Object.keys(mediaItem)
        .filter((key) => !isNaN(key))
        .sort((a, b) => Number(a) - Number(b));
      if (chars.length > 0) {
        return chars.map((key) => mediaItem[key]).join('');
      }
    }

    return null;
  };

  // Once approved or rejected, can only toggle isActive
  const canApprove = service.approvalStatus === 'pending';
  const canReject = service.approvalStatus === 'pending';

  const handleApprove = () => {
    updateStatus(
      {
        serviceId: service._id,
        payload: { approvalStatus: 'approved' },
      },
      {
        onSuccess: () => {
          setIsViewModalOpen(false);
        },
      },
    );
  };

  const handleReject = () => {
    if (!rejectionReason.trim()) {
      return;
    }
    updateStatus(
      {
        serviceId: service._id,
        payload: {
          approvalStatus: 'rejected',
          rejectionReason: rejectionReason.trim(),
        },
      },
      {
        onSuccess: () => {
          setShowRejectInput(false);
          setRejectionReason('');
          setIsViewModalOpen(false);
        },
      },
    );
  };

  const handleRejectClick = () => {
    setShowRejectInput(true);
  };

  const handleCancelReject = () => {
    setShowRejectInput(false);
    setRejectionReason('');
  };

  const getApprovalStatusIcon = (status) => {
    switch (status) {
      case 'approved':
        return (
          <CheckCircle
            size={16}
            className='text-green-600 dark:text-green-400'
          />
        );
      case 'rejected':
        return <XCircle size={16} className='text-red-600 dark:text-red-400' />;
      default:
        return (
          <AlertCircle
            size={16}
            className='text-yellow-600 dark:text-yellow-400'
          />
        );
    }
  };

  return (
    <div className='fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4'>
      <div className='bg-card border border-border rounded-lg w-full max-w-4xl max-h-[90vh] overflow-y-auto custom-scrollbar'>
        {/* Header */}
        <div className='sticky top-0 bg-card border-b border-border px-6 py-4 flex items-center justify-between'>
          <div className='flex items-center space-x-3'>
            <div className='p-2 bg-primary/10 rounded-lg'>
              <Wrench size={20} className='text-primary' />
            </div>
            <h2 className='text-lg font-semibold'>Service Details</h2>
          </div>
          <button
            onClick={() => setIsViewModalOpen(false)}
            className='p-1 hover:bg-muted rounded-md transition-colors'
          >
            <X size={20} />
          </button>
        </div>

        {/* Content */}
        <div className='p-6 space-y-6'>
          {/* Images Section */}
          {service.images && service.images.length > 0 && (
            <div className='space-y-2'>
              <label className='text-xs font-medium text-muted-foreground uppercase'>
                Service Images
              </label>
              <div className='grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3'>
                {service.images.map((image, index) => {
                  const imageUrl = getMediaUrl(image);
                  return imageUrl ? (
                    <img
                      key={index}
                      src={imageUrl}
                      alt={`Service image ${index + 1}`}
                      className='w-full h-32 object-cover rounded-lg border border-border'
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.parentElement.innerHTML = `<div class='w-full h-32 rounded-lg bg-muted flex items-center justify-center'><svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="text-muted-foreground"><path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z"></path></svg></div>`;
                      }}
                    />
                  ) : (
                    <div
                      key={index}
                      className='w-full h-32 rounded-lg bg-muted flex items-center justify-center'
                    >
                      <Wrench size={20} className='text-muted-foreground' />
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Videos Section */}
          {service.videos && service.videos.length > 0 && (
            <div className='space-y-2'>
              <label className='text-xs font-medium text-muted-foreground uppercase'>
                Service Videos
              </label>
              <div className='grid grid-cols-1 md:grid-cols-2 gap-3'>
                {service.videos.map((video, index) => {
                  const videoUrl = getMediaUrl(video);
                  return videoUrl ? (
                    <video
                      key={index}
                      src={videoUrl}
                      controls
                      className='w-full h-48 rounded-lg border border-border'
                    />
                  ) : (
                    <div
                      key={index}
                      className='w-full h-48 rounded-lg bg-muted flex items-center justify-center'
                    >
                      <p className='text-sm text-muted-foreground'>
                        Video unavailable
                      </p>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Basic Info */}
          <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
            <div className='space-y-1'>
              <label className='text-xs font-medium text-muted-foreground uppercase flex items-center space-x-1'>
                <Wrench size={14} />
                <span>Service Name</span>
              </label>
              <p className='text-sm font-medium break-words'>{service.name || 'N/A'}</p>
            </div>

            {/* <div className='space-y-1'>
              <label className='text-xs font-medium text-muted-foreground uppercase'>
                Slug
              </label>
              <p className='text-sm font-mono text-muted-foreground break-words'>
                {service.slug || 'N/A'}
              </p>
            </div> */}
          </div>

          {/* Description */}
          {service.description && (
            <div className='space-y-2'>
              <label className='text-xs font-medium text-muted-foreground uppercase'>
                Description
              </label>
              <div className='bg-muted rounded-lg p-4'>
                <p className='text-sm break-words'>{service.description}</p>
              </div>
            </div>
          )}

          {/* Short Description */}
          {service.shortDescription && (
            <div className='space-y-2'>
              <label className='text-xs font-medium text-muted-foreground uppercase'>
                Short Description
              </label>
              <div className='bg-muted rounded-lg p-4'>
                <p className='text-sm'>{service.shortDescription}</p>
              </div>
            </div>
          )}

          {/* Seller Info */}
          <div className='bg-muted rounded-lg p-4 space-y-3'>
            <label className='text-xs font-medium text-muted-foreground uppercase flex items-center space-x-1'>
              <User size={14} />
              <span>Seller Information</span>
            </label>
            <div className='grid grid-cols-1 md:grid-cols-2 gap-3'>
              <div>
                <p className='text-xs text-muted-foreground mb-1'>Name:</p>
                <p className='text-sm font-medium'>
                  {service.sellerId?.firstName || service.sellerId?.lastName
                    ? `${service.sellerId?.firstName || ''} ${service.sellerId?.lastName || ''}`.trim()
                    : 'N/A'}
                </p>
              </div>
              <div>
                <p className='text-xs text-muted-foreground mb-1'>Email:</p>
                <p className='text-sm'>{service.sellerId?.email || 'N/A'}</p>
              </div>
              <div>
                <p className='text-xs text-muted-foreground mb-1'>Phone:</p>
                <p className='text-sm'>
                  {service.sellerId?.phoneNumber || 'N/A'}
                </p>
              </div>
            </div>
          </div>

          {/* Category Info */}
          <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
            <div className='space-y-1'>
              <label className='text-xs font-medium text-muted-foreground uppercase'>
                Category
              </label>
              <p className='text-sm font-medium'>
                {service.categoryId?.name?.english ||
                  service.category?.name?.english ||
                  'N/A'}
              </p>
            </div>
            <div className='space-y-1'>
              <label className='text-xs font-medium text-muted-foreground uppercase'>
                Subcategory
              </label>
              <p className='text-sm font-medium'>
                {service.subcategoryId?.name?.english ||
                  service.subcategory?.name?.english ||
                  'N/A'}
              </p>
            </div>
          </div>

          {/* Pricing Info */}
          <div className='bg-green-500/5 border border-green-500/20 rounded-lg p-4 space-y-3'>
            <label className='text-xs font-medium text-muted-foreground uppercase flex items-center space-x-1'>
              <IndianRupee size={14} />
              <span>Pricing Details</span>
            </label>
            <div className='grid grid-cols-2 md:grid-cols-4 gap-4'>
              {/* <div>
                <p className='text-xs text-muted-foreground mb-1'>Type:</p>
                <p className='text-sm font-medium capitalize'>
                  {service.pricing?.type || 'N/A'}
                </p>
              </div> */}
              <div>
                <p className='text-xs text-muted-foreground mb-1'>
                  Service Price:
                </p>
                <p className='text-sm font-medium'>
                  {service.pricing?.currency || 'INR'}{' '}
                  {service.pricing?.basePrice?.toFixed(2) || '0.00'}
                </p>
              </div>
              {/* <div>
                <p className='text-xs text-muted-foreground mb-1'>
                  Discounted Price:
                </p>
                <p className='text-lg font-bold text-green-600 dark:text-green-400'>
                  {service.pricing?.currency || 'INR'}{' '}
                  {service.pricing?.discountedPrice?.toFixed(2) || '0.00'}
                </p>
              </div> */}
              {/* <div>
                <p className='text-xs text-muted-foreground mb-1'>
                  Discount %:
                </p>
                <p className='text-sm font-medium'>
                  {service.pricing?.discountPercentage > 0 ? (
                    <span className='px-2 py-1 bg-green-500/10 text-green-600 dark:text-green-400 rounded-full text-xs font-medium'>
                      {service.pricing?.discountPercentage}% OFF
                    </span>
                  ) : (
                    '0%'
                  )}
                </p>
              </div> */}
            </div>
          </div>

          {/* Duration */}
          {service.duration > 0 && (
            <div className='space-y-1'>
              <label className='text-xs font-medium text-muted-foreground uppercase flex items-center space-x-1'>
                <Clock size={14} />
                <span>Duration</span>
              </label>
              <p className='text-sm font-medium'>{service.duration} minutes</p>
            </div>
          )}

          {/* Approval Status */}
          <div className='bg-muted rounded-lg p-4 space-y-3'>
            <label className='text-xs font-medium text-muted-foreground uppercase'>
              Approval Status
            </label>
            <div className='flex items-center space-x-2'>
              {getApprovalStatusIcon(service.approvalStatus)}
              <span
                className={`px-3 py-1 text-xs font-medium rounded-full capitalize ${
                  service.approvalStatus === 'approved'
                    ? 'bg-green-500/10 text-green-600 dark:text-green-400'
                    : service.approvalStatus === 'rejected'
                      ? 'bg-red-500/10 text-red-600 dark:text-red-400'
                      : 'bg-yellow-500/10 text-yellow-600 dark:text-yellow-400'
                }`}
              >
                {service.approvalStatus}
              </span>
            </div>
            {service.approvedAt && (
              <div>
                <p className='text-xs text-muted-foreground mb-1'>
                  Approved At:
                </p>
                <p className='text-sm'>{formatDateTime(service.approvedAt)}</p>
              </div>
            )}
            {service.rejectionReason && (
              <div>
                <p className='text-xs text-muted-foreground mb-1'>
                  Rejection Reason:
                </p>
                <p className='text-sm text-red-600 dark:text-red-400'>
                  {service.rejectionReason}
                </p>
              </div>
            )}
          </div>

          {/* Status Badges */}
          <div className='space-y-2'>
            <label className='text-xs font-medium text-muted-foreground uppercase'>
              Service Status
            </label>
            <div className='flex flex-wrap gap-2'>
              <span
                className={`px-3 py-1 text-xs font-medium rounded-full ${
                  service.isActive
                    ? 'bg-green-500/10 text-green-600 dark:text-green-400'
                    : 'bg-gray-500/10 text-gray-600 dark:text-gray-400'
                }`}
              >
                {service.isActive ? 'Active' : 'Inactive'}
              </span>
              {/* <span
                className={`px-3 py-1 text-xs font-medium rounded-full ${
                  service.isPopular
                    ? 'bg-orange-500/10 text-orange-600 dark:text-orange-400'
                    : 'bg-gray-500/10 text-gray-600 dark:text-gray-400'
                }`}
              >
                {service.isPopular ? 'Popular' : 'Regular'}
              </span> */}
              {/* <span
                className={`px-3 py-1 text-xs font-medium rounded-full ${
                  service.isFeatured
                    ? 'bg-yellow-500/10 text-yellow-600 dark:text-yellow-400'
                    : 'bg-gray-500/10 text-gray-600 dark:text-gray-400'
                }`}
              >
                {service.isFeatured ? 'Featured' : 'Normal'}
              </span> */}
            </div>
          </div>

          {/* Statistics */}
          {/* {service.stats && (
            <div className='bg-purple-500/5 border border-purple-500/20 rounded-lg p-4 space-y-3'>
              <label className='text-xs font-medium text-muted-foreground uppercase flex items-center space-x-1'>
                <ShoppingCart size={14} />
                <span>Booking Statistics</span>
              </label>
              <div className='grid grid-cols-3 gap-4'>
                <div>
                  <p className='text-xs text-muted-foreground mb-1'>
                    Total Bookings:
                  </p>
                  <p className='text-lg font-bold'>
                    {service.stats.totalBookings || 0}
                  </p>
                </div>
                <div>
                  <p className='text-xs text-muted-foreground mb-1'>
                    Completed:
                  </p>
                  <p className='text-lg font-bold text-green-600 dark:text-green-400'>
                    {service.stats.completedBookings || 0}
                  </p>
                </div>
                <div>
                  <p className='text-xs text-muted-foreground mb-1'>
                    Cancelled:
                  </p>
                  <p className='text-lg font-bold text-red-600 dark:text-red-400'>
                    {service.stats.cancelledBookings || 0}
                  </p>
                </div>
              </div>
            </div>
          )} */}

          {/* Ratings */}
          {/* {service.ratings && (
            <div className='bg-yellow-500/5 border border-yellow-500/20 rounded-lg p-4'>
              <div className='flex items-center justify-between'>
                <div className='flex items-center space-x-2'>
                  <Star
                    size={16}
                    className='text-yellow-600 dark:text-yellow-400'
                  />
                  <span className='text-xs font-medium text-muted-foreground uppercase'>
                    Ratings
                  </span>
                </div>
                <div className='text-right'>
                  <p className='text-2xl font-bold text-yellow-600 dark:text-yellow-400'>
                    {service.ratings.averageRating?.toFixed(1) || '0.0'}
                  </p>
                  <p className='text-xs text-muted-foreground'>
                    {service.ratings.totalRatings || 0} ratings
                  </p>
                </div>
              </div>
            </div>
          )} */}

          {/* Operating Hours */}
          {service.operatingHours && service.operatingHours.length > 0 && (
            <div className='space-y-3'>
              <label className='text-xs font-medium text-muted-foreground uppercase'>
                Operating Hours
              </label>
              <div className='bg-muted rounded-lg p-4 space-y-2'>
                {service.operatingHours.map((hours, index) => (
                  <div
                    key={index}
                    className='flex items-center justify-between py-2 border-b border-border last:border-0'
                  >
                    <span className='text-sm font-medium capitalize'>
                      {hours.day}
                    </span>
                    {hours.isOpen ? (
                      <div className='text-sm text-muted-foreground'>
                        {hours.openTime} - {hours.closeTime}
                      </div>
                    ) : (
                      <span className='text-sm text-red-600 dark:text-red-400'>
                        Closed
                      </span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Timestamps */}
          <div className='grid grid-cols-1 md:grid-cols-2 gap-4 pt-4 border-t border-border'>
            <div className='space-y-1'>
              <label className='text-xs font-medium text-muted-foreground uppercase flex items-center space-x-1'>
                <Calendar size={14} />
                <span>Created At</span>
              </label>
              <p className='text-sm'>{formatDateTime(service.createdAt)}</p>
            </div>

            <div className='space-y-1'>
              <label className='text-xs font-medium text-muted-foreground uppercase flex items-center space-x-1'>
                <Calendar size={14} />
                <span>Updated At</span>
              </label>
              <p className='text-sm'>{formatDateTime(service.updatedAt)}</p>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className='px-6 py-4 border-t border-border'>
          {/* Rejection Input Section */}
          {showRejectInput && (
            <div className='mb-4 space-y-3'>
              <div className='bg-red-500/5 border border-red-500/20 rounded-lg p-4'>
                <label className='block text-sm font-medium mb-2'>
                  Rejection Reason *
                </label>
                <textarea
                  value={rejectionReason}
                  onChange={(e) => setRejectionReason(e.target.value)}
                  placeholder='Please provide a reason for rejecting this service...'
                  className='w-full px-3 py-2 text-sm border border-border rounded-md bg-background min-h-[80px] resize-none focus:outline-none focus:ring-2 focus:ring-red-500'
                  disabled={isPending}
                  autoFocus
                />
                {!rejectionReason.trim() && (
                  <p className='text-xs text-red-600 dark:text-red-400 mt-1'>
                    Rejection reason is required
                  </p>
                )}
              </div>
              <div className='flex justify-end space-x-2'>
                <button
                  onClick={handleCancelReject}
                  disabled={isPending}
                  className='px-4 py-2 text-sm border border-border rounded-md hover:bg-accent transition-colors disabled:opacity-50'
                >
                  Cancel
                </button>
                <button
                  onClick={handleReject}
                  disabled={isPending || !rejectionReason.trim()}
                  className='flex items-center space-x-2 px-4 py-2 text-sm bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed'
                >
                  {isPending ? (
                    <>
                      <div className='animate-spin rounded-full h-4 w-4 border-b-2 border-white' />
                      <span>Rejecting...</span>
                    </>
                  ) : (
                    <>
                      <ThumbsDown size={16} />
                      <span>Confirm Reject</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className='flex justify-between'>
            <div className='flex space-x-2'>
              {canApprove && !showRejectInput && (
                <button
                  onClick={handleApprove}
                  disabled={isPending}
                  className='flex items-center space-x-2 px-4 py-2 text-sm bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed'
                >
                  {isPending ? (
                    <>
                      <div className='animate-spin rounded-full h-4 w-4 border-b-2 border-white' />
                      <span>Approving...</span>
                    </>
                  ) : (
                    <>
                      <ThumbsUp size={16} />
                      <span>Approve</span>
                    </>
                  )}
                </button>
              )}
              {canReject && !showRejectInput && (
                <button
                  onClick={handleRejectClick}
                  disabled={isPending}
                  className='flex items-center space-x-2 px-4 py-2 text-sm bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed'
                >
                  <ThumbsDown size={16} />
                  <span>Reject</span>
                </button>
              )}
            </div>
            <button
              onClick={() => setIsViewModalOpen(false)}
              disabled={isPending}
              className='px-4 py-2 text-sm border border-border rounded-md hover:bg-accent transition-colors disabled:opacity-50'
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
